import { FC, Fragment, useContext, useState } from 'react';
import { BookResponse } from 'ts/hotel/interfaces/BookResponse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faHotel, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Rating from 'components/ui/Rating/Rating';
import { getCurrency, formatPriceCurrency, checkAHAVI } from 'utils/utils';
import { formatPriceHotel, getHotelDisplaySettings } from 'utils/hotel';
import { dateFormat } from 'utils/shared/utils';
import HandleFee from 'components/shared/HandleFee';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import MoSpecialRequestModal from 'components/shared/MyOrders/MoActions/MoSpecialRequestModal';
import MoSpecialRequestShort from 'components/shared/MyOrders/MoActions/MoSpecialRequestShort';
import { useTranslation } from 'react-i18next';

import { getPriceModesList, imgFolder } from 'constants/hotel';
import { BookModel } from 'ts/hotel/classes/BookModel';
import { useHotelProposal } from 'hooks/useProposal';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b } from 'constants/generic';

interface BookSummaryProps {
  book: BookResponse;
  isSuccess?: boolean;
  setSpecialRequestMessage?: (message: string) => void;
  paxes?: BookModel;
  isSubmitted?: boolean;
  isApartmentAccepted?: boolean;
  setIsApartmentAccepted?: (isApartmentAccepted: boolean) => void;
}

const BookSummary: FC<BookSummaryProps> = ({
  book,
  isSubmitted,
  isApartmentAccepted,
  setIsApartmentAccepted,
  ...props
}) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const { hotelIndex } = useContext(HotelConfigContext);
  const { user, cdnUrl } = useContext(MainContext);
  const { priceMode, currency } = getHotelDisplaySettings(hotelIndex);
  const [specialRequestList, setSpecialRequestList] = useState<{ code: string; data: any }[]>([]);
  const { toggleHotelProposal, isHotelInProposal } = useHotelProposal();

  const addSpecialRequest = (message: string) => {
    props.setSpecialRequestMessage?.(message);
    setOpenModal(false);
  };

  const isSuccess =
    props.isSuccess && book.order?.order?.segments?.[0]?.rooms?.[0]?.sysStatus?.code === 'OK';

  const netPriceModeLabel =
    (user && getPriceModesList(user).find((mode) => mode.value === priceMode)?.label) ||
    (priceMode === '1' ? 'Net' : 'Net.T');

  return (
    <div className="details-summary-wr position-relative">
      {!props.isSuccess && (
        <ProposalHeart
          onClickPropsal={() =>
            toggleHotelProposal({ ...book?.hotel, roomClasses: [book?.roomClass] })
          }
          active={isHotelInProposal(book?.hotel)}
        />
      )}
      <div
        className="details-summary book-summary default-shadow mb-20 text-lh position-relative"
        style={{
          background: isSuccess ? '#d4edda' : 'var(--color-bg-light)',
        }}
        id="order-summary"
      >
        <div className="title-underline title-header-grey text-lg">
          {t('passenger.order_details')}
        </div>{' '}
        {isSuccess && (
          <img
            src={cdnUrl + imgFolder + 'frontend/success_badge.png'}
            alt="sussess"
            className="hotel-success-badge"
          />
        )}
        <div className="text-md mb-5">
          <FontAwesomeIcon icon={faHotel} className="mr-5" size={'sm'} />
          {t('hotel.details')}
        </div>
        <div className="book-rooms-single">
          <div className="title mb-5 text-md text-black">{book.hotel.item.name}</div>
          <div className="text-light mb-5">
            <FontAwesomeIcon icon={faLocationDot} className="mr-5" />
            {book.hotel.addressInfo.address}
          </div>
          <Rating className="stars mb-5" value={book.hotel.item.star} />
          <div className="mb-5">
            {t('mo.check_in')}:{' '}
            <span className="text-bold">
              {dateFormat(book.roomClass.checkIn, 'EEE - dd MMMM, yyyy')}
            </span>
          </div>
          <div className="mb-5">
            {t('mo.check_out')}:{' '}
            <span className="text-bold">
              {dateFormat(book.roomClass.checkOut, 'EEE - dd MMMM, yyyy')}
            </span>
          </div>
          <div className="mb-5">
            {t('hotel.nights_up')}: <span className="text-bold">{book.roomClass.nights}</span>
          </div>
          <div className="mb-20">
            {t('passenger.room_count')}:{' '}
            <span className="text-bold">{book.roomClass.hotelRooms.length}</span>
          </div>
        </div>
        <div className="book-rooms-summary">
          <HandleFee name="Hotels" />
          <div className="text-md mb-5">
            <FontAwesomeIcon icon={faBookmark} className="mr-5" size={'sm'} />
            {t('passenger.booking_summary')}
          </div>
          {book.roomClass.hotelRooms.map((room, index) => (
            <div className="mb-5 title text-md text-black" key={index}>
              <div
                className={checkAHAVI(room.desc + book.roomClass.board.basis.desc) ? 'rtl' : 'ltr'}
              >
                {room.desc} {book.roomClass.board.basis.desc}
              </div>
            </div>
          ))}

          {user?.branch?.hotelDisplaySupplierName && (
            <div className="text-light text-normal">{book.roomClass?.suppDesc || ((book.hotel.item.sysSupp ? book.hotel.item.sysSupp + ' ' : '') + book.hotel.item.sysSuppCode)}</div>
          )}

          {props.isSuccess &&
            book.order?.order?.segments?.[0]?.rooms.map((rc: any) => (
              <div className="mb-5 text-bold text-black">
                {t('hotel.room_status')}: {rc.sysStatus?.code}
              </div>
            ))}
        </div>
        <div className="book-summary-total title-footer-grey">
          <span className="title text-lg">{t('total')}: </span>
          {!props.isSuccess && (
            <span>
              <span className="text-lg text-contrast mr-5">
                {formatPriceHotel(book.roomClass.price, currency, undefined, true)}
              </span>
              {b2b &&
                book.roomClass.price.originalCurrency.toLowerCase() !== currency.toLowerCase() && (
                  <span className="">
                    (
                    {formatPriceCurrency(
                      book.roomClass.price.originalValue,
                      getCurrency(book.roomClass.price.originalCurrency),
                      true
                    )}
                    )
                  </span>
                )}
            </span>
          )}
          {props.isSuccess && (
            <span className="text-lg text-contrast mr-5">
              {formatPriceCurrency(book.totalPrice, getCurrency(book.currency), true)}
            </span>
          )}

        </div>
        {!props.isSuccess && priceMode !== '0' && (
          <div className="book-summary-total title-footer-grey">
            <span className="title text-lg">{netPriceModeLabel}: </span>
            <span
              className="text-lg text-contrast mr-5"
              dangerouslySetInnerHTML={{
                __html: formatPriceHotel(book.roomClass.price, currency, priceMode, true),
              }}
            />
          </div>
        )}
      </div>

      {!props.isSuccess && (
        <Fragment>
          {b2b && (
            <div className="details-summary book-summary default-shadow mt-20">
              <div className="title-underline text-lg mb-10 title-header-grey">
                {t('hotel.please_add_special_request')}
              </div>
              <Button title={t('hotel.add_special_request')} onClick={() => setOpenModal(true)} />
              {openModal && (
                <MoSpecialRequestModal
                  book={book}
                  paxes={props.paxes}
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  onSubmit={addSpecialRequest}
                  specialRequestList={specialRequestList}
                  setSpecialRequestList={setSpecialRequestList}
                />
              )}

              <MoSpecialRequestShort
                book={book}
                paxes={props.paxes}
                specialRequestList={specialRequestList}
                setSpecialRequestList={setSpecialRequestList}
                onSubmit={addSpecialRequest}
              />
            </div>
          )}

          {book.roomClass.hotelRooms?.[0]?.typ === 'apartment' && (
            <div className="details-summary book-summary default-shadow mt-20">
              <div className="title-underline text-lg mb-10">{t('passenger.important_notes')}</div>

              <Checkbox
                label={t('passenger.apartment_notes')}
                checked={isApartmentAccepted}
                onChange={() => setIsApartmentAccepted?.(!isApartmentAccepted)}
                hasError={!isApartmentAccepted && isSubmitted}
                className="text-lh"
              />
            </div>
          )}
        </Fragment>
      )}

    </div>
  );
};

export default withErrorBoundary(BookSummary, 'BookSummary');
